import { useMemo, type Dispatch, type SetStateAction } from "react"
import { DatePicker, Input } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { Select } from "components/ui/select/Select"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { PlusIcon } from "components/ui/icons/plus/PlusIcon"
import { CalendarIcon } from "components/ui/icons/calendar/CalendarIcon"
import { DATE_PICKER_FORMAT } from "helpers/dateFormat.helper"
import { useGetSeries } from "hooks/api/series/series.hook"
import { useGetComponentOptions } from "hooks/api/quotation/quotations.hook"
import { createEmptyDeliveryDate, isDeliveryDateIncomplete } from "./quotationConfiguration.helper"
import type { DefaultOptionType } from "antd/es/select"
import type { TQuotationComponentOption, TQuotationComponentSlug } from "types/quotations.type"
import type { TOnChangeInput } from "types/default.type"
import style from "./QuotationConfiguration.module.scss"

const {
	wrapper,
	field,
	label,
	required,
	select,
	grid,
	gridItem,
	section,
	sectionTitle,
	dateList,
	dateRow,
	dateNameWrapper,
	dateSelectWrapper,
	removeButton,
	removeIcon,
	addButton,
} = style

export type TQuotationDeliveryDate = { key: string; id: number | null; date?: string; name?: string }

export type TQuotationConfig = {
	seriesId?: number
	pullCollectionId?: number
	drawerBoxesMaterialId?: number
	drawerSlideTypeId?: number
	hingesTypeId?: number
	deliveryDates: TQuotationDeliveryDate[]
}

type Props = {
	config: TQuotationConfig
	setConfig: Dispatch<SetStateAction<TQuotationConfig>>
	quotationId?: number
	// When set, removing/changing an already-saved date row (one with a non-null id) is delegated to
	// the parent (e.g. to show a confirmation) instead of mutating state directly. New rows always mutate.
	onRequestRemoveExistingDate?: (index: number) => void
	onRequestChangeExistingDate?: (index: number, date: string) => void
}

const COMPONENT_SLUGS: TQuotationComponentSlug[] = [
	"pull_collection",
	"drawer_boxes_material",
	"drawer_slide_type",
	"hinges_type",
]

const toOptions = (items?: TQuotationComponentOption[]): DefaultOptionType[] =>
	(items ?? []).map((item) => ({ label: item.name, value: item.id }))

export const QuotationConfiguration = ({
	config,
	setConfig,
	quotationId,
	onRequestRemoveExistingDate,
	onRequestChangeExistingDate,
}: Props) => {
	const { seriesId, pullCollectionId, drawerBoxesMaterialId, drawerSlideTypeId, hingesTypeId, deliveryDates } = config

	const { data: series } = useGetSeries()
	const { data: componentOptions } = useGetComponentOptions({
		seriesId,
		components: COMPONENT_SLUGS,
		quotationId,
	})

	const seriesOptions: DefaultOptionType[] = useMemo(
		() => (series ?? []).map((s) => ({ label: s.series_name, value: s.id })),
		[series],
	)

	const dependentOptions = useMemo(
		() => ({
			pull: toOptions(componentOptions?.pull_collection),
			boxes: toOptions(componentOptions?.drawer_boxes_material),
			slide: toOptions(componentOptions?.drawer_slide_type),
			hinges: toOptions(componentOptions?.hinges_type),
		}),
		[componentOptions],
	)

	const onSeriesChange = (value: string | number) => {
		setConfig((prev) => ({
			...prev,
			seriesId: Number(value),
			pullCollectionId: undefined,
			drawerBoxesMaterialId: undefined,
			drawerSlideTypeId: undefined,
			hingesTypeId: undefined,
		}))
	}

	const isDependentDisabled = !seriesId

	const updateDateAt = (index: number, value: Dayjs | null) => {
		const newDate = value ? value.format("YYYY-MM-DD") : undefined
		// Changing an already-saved date is delegated to the parent (confirmation) when a handler is set.
		if (newDate && deliveryDates[index]?.id != null && onRequestChangeExistingDate) {
			onRequestChangeExistingDate(index, newDate)
			return
		}
		setConfig((prev) => ({
			...prev,
			deliveryDates: prev.deliveryDates.map((d, i) => (i === index ? { ...d, date: newDate } : d)),
		}))
	}

	const updateNameAt = (index: number, e: TOnChangeInput) => {
		const name = e.target.value
		setConfig((prev) => ({
			...prev,
			deliveryDates: prev.deliveryDates.map((d, i) => (i === index ? { ...d, name } : d)),
		}))
	}

	const addDate = () => {
		setConfig((prev) => ({ ...prev, deliveryDates: [...prev.deliveryDates, createEmptyDeliveryDate()] }))
	}

	const removeDateAt = (index: number) => {
		// Removing an already-saved date is delegated to the parent (confirmation) when a handler is set.
		if (deliveryDates[index]?.id != null && onRequestRemoveExistingDate) {
			onRequestRemoveExistingDate(index)
			return
		}
		setConfig((prev) => ({ ...prev, deliveryDates: prev.deliveryDates.filter((_, i) => i !== index) }))
	}

	return (
		<div className={wrapper} data-testid="quotation-configuration">
			<div className={field}>
				<div className={label}>
					<span>Series</span>
					<span className={required}>*</span>
				</div>
				<Select
					options={seriesOptions}
					value={seriesId}
					onChange={onSeriesChange}
					className={select}
					variant="outlined"
					placeholder="Select type"
					showSearch
					optionFilterProp="label"
				/>
			</div>

			<div className={grid}>
				<div className={gridItem}>
					<span className={label}>Pull collection</span>
					<Select
						options={dependentOptions.pull}
						value={pullCollectionId}
						onChange={(value) => setConfig((prev) => ({ ...prev, pullCollectionId: Number(value) }))}
						className={select}
						variant="outlined"
						placeholder="Select type"
						disabled={isDependentDisabled}
					/>
				</div>
				<div className={gridItem}>
					<span className={label}>Drawer boxes material</span>
					<Select
						options={dependentOptions.boxes}
						value={drawerBoxesMaterialId}
						onChange={(value) => setConfig((prev) => ({ ...prev, drawerBoxesMaterialId: Number(value) }))}
						className={select}
						variant="outlined"
						placeholder="Select type"
						disabled={isDependentDisabled}
					/>
				</div>
				<div className={gridItem}>
					<span className={label}>Drawer slide type</span>
					<Select
						options={dependentOptions.slide}
						value={drawerSlideTypeId}
						onChange={(value) => setConfig((prev) => ({ ...prev, drawerSlideTypeId: Number(value) }))}
						className={select}
						variant="outlined"
						placeholder="Select type"
						disabled={isDependentDisabled}
					/>
				</div>
				<div className={gridItem}>
					<span className={label}>Hinges type</span>
					<Select
						options={dependentOptions.hinges}
						value={hingesTypeId}
						onChange={(value) => setConfig((prev) => ({ ...prev, hingesTypeId: Number(value) }))}
						className={select}
						variant="outlined"
						placeholder="Select type"
						disabled={isDependentDisabled}
					/>
				</div>
			</div>

			<section className={section}>
				<h3 className={sectionTitle}>Delivery dates</h3>
				<div className={dateList}>
					{deliveryDates.map((row, index) => {
						const incomplete = isDeliveryDateIncomplete(row)
						return (
							<div key={row.key} className={dateRow}>
								<div className={dateSelectWrapper}>
									<DatePicker
										value={row.date ? dayjs(row.date) : null}
										onChange={(value) => updateDateAt(index, value)}
										variant="outlined"
										placeholder="Select date"
										suffixIcon={<CalendarIcon />}
										format={DATE_PICKER_FORMAT}
										allowClear={false}
										disabledDate={(current) => current.isBefore(dayjs().startOf("day"))}
										status={incomplete && !row.date ? "error" : undefined}
									/>
								</div>
								<div className={dateNameWrapper}>
									<Input
										value={row.name ?? ""}
										onChange={(e) => updateNameAt(index, e)}
										variant="outlined"
										placeholder="Project name"
										size="middle"
										status={incomplete && !row.name?.trim() ? "error" : undefined}
									/>
								</div>
								<button
									type="button"
									className={removeButton}
									onClick={() => removeDateAt(index)}
									aria-label="Remove delivery date"
								>
									<CloseIcon className={removeIcon} />
								</button>
							</div>
						)
					})}
				</div>
				<button type="button" className={addButton} onClick={addDate}>
					<PlusIcon />
					Add date
				</button>
			</section>
		</div>
	)
}
